'use client'

/**
 * Training Completion Fail-Safe Hook (Issue #3228)
 *
 * Reads the `complete_training` query parameter from the URL and
 * calls completeTrainingItem() to guarantee the training step is
 * marked complete when the page loads.
 *
 * This is a belt-and-suspenders mechanism: the primary completion
 * detection via page.viewed events remains, but this ensures items
 * are completed even if the event tracker fails to fire.
 *
 * After completion, the query parameter is removed from the URL.
 */

import { useContext, useEffect, useRef } from 'react'
import { useSearchParams } from 'next/navigation'
import { completeTrainingItem } from '@/app/actions/training-items'
import { TrainingContext } from '@/contexts/training-context'
import { TRAINING_COMPLETION_PARAM } from '@/lib/training-utils'

/**
 * Hook that reads complete_training from URL and triggers completion.
 *
 * Place this in a component mounted at the root layout level so it
 * works on any page the user navigates to.
 */
export function useTrainingCompletionFailsafe(): void {
  // useSearchParams can return null during prerender — optional chaining
  // avoids throwing mid-render and triggering React #310 (issue #5520).
  const searchParams = useSearchParams()
  const trainingContext = useContext(TrainingContext)
  const processedItemId = useRef<string | null>(null)

  const itemId = searchParams?.get(TRAINING_COMPLETION_PARAM) ?? null

  useEffect(() => {
    if (!itemId || processedItemId.current === itemId) {
      return
    }

    processedItemId.current = itemId

    async function completeAndClean() {
      try {
        await completeTrainingItem(itemId as string)
        // Refresh training context so hints update immediately
        await trainingContext?.refresh()
      } catch {
        // Non-critical: the primary event tracker may still complete it
      }

      // Clean the URL by removing the query parameter
      const url = new URL(window.location.href)
      url.searchParams.delete(TRAINING_COMPLETION_PARAM)
      window.history.replaceState({}, '', url.toString())
    }

    completeAndClean()
  }, [itemId, trainingContext])
}
